import { Injectable } from '@angular/core';
import { ServiceBase } from 'app/shared/serviceBase';
import { AuthenticationService } from 'app/shared/services/authentication-service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { QuestionAddModel } from '../models/question-add-model';
import { QuestionDetailsModel } from '../models/question-details-model';
import { QuestionListModel } from '../models/question-list.model';
import { ResponseTypeModel } from '../models/response-type.model';
import { QuestionBankAddModel } from '../models/question-bank-add.model';
import { QuestionBankDetailsModel } from '../models/question-bank-details.model';
import { QuestionBankListModel } from '../models/question-bank-list.model';
import { QuestionBankQuestionListModel } from '../models/question-bank-question-list.model';
import { QuestionBankQuestionAddModel } from '../models/question-bank-question-add.model';
import { QuestionBankQuestionReorderModel } from '../models/question-bank-question-reorder.model';
import { QuestionAnswerModel } from '../models/question-answer.model';

@Injectable()
export class QuestionService extends ServiceBase {
    private commonUrl: string = this.url + '/Question/';
    constructor(
        private _http: HttpClient,
        private _authentication: AuthenticationService
    ) {
        super();
    }

    create(model: QuestionAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    update(model: QuestionDetailsModel): Observable<Response> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    getAll(): Observable<QuestionListModel[]> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .get<QuestionListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    get(id: string): Observable<QuestionDetailsModel> {
        const completeUrl: string = this.commonUrl + id;
        return this._http
            .get<QuestionDetailsModel>(completeUrl).pipe(
                catchError(this.handleError));
    }

    getResponseTypes(): Observable<ResponseTypeModel[]> {
        const completeUrl: string = this.commonUrl + 'response-types';
        return this._http
            .get<ResponseTypeModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCurrentQuestionAnswers(questionId: string): Observable<QuestionAnswerModel[]> {
        const completeUrl: string = this.commonUrl + 'question-answers/' + questionId;
        return this._http
            .get<QuestionAnswerModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getQuestionAnswers(): Observable<QuestionAnswerModel[]> {
        const completeUrl: string = this.commonUrl + 'question-answers';
        return this._http
            .get<QuestionAnswerModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    createQuestionBank(model: QuestionBankAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'question-bank';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    updateQuestionBank(model: QuestionBankDetailsModel): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'question-bank';
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    getAllQuestionBank(): Observable<QuestionBankListModel[]> {
        const completeUrl: string = this.commonUrl + 'question-bank';
        return this._http
            .get<QuestionBankListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getQuestionBank(id: string): Observable<QuestionBankDetailsModel> {
        const completeUrl: string = this.commonUrl + 'question-bank/' + id;
        return this._http
            .get<QuestionBankDetailsModel>(completeUrl).pipe(
                catchError(this.handleError));
    }

    getQuestionBankQuestions(questionBankId: string): Observable<QuestionBankQuestionListModel[]> {
        const completeUrl: string = this.commonUrl + 'question-bank-question/' + questionBankId;
        return this._http
            .get<QuestionBankQuestionListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    deleteQuestionBankQuestion(questionBankQuestionId: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'question-bank-question/' + questionBankQuestionId;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    deleteQuestion(questionId: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'question/' + questionId;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    deleteQuestionBank(questionBankId: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'question-bank/' + questionBankId;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    addQuestionBankQuestion(model: QuestionBankQuestionAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'question-bank-question';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    reorderQuestionBankQuestion(model: QuestionBankQuestionReorderModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'question-bank-question-reorder';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    toggleMandatoryQuestionBankQuestion(model: QuestionBankQuestionListModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'question-bank-question-toggle-isMandatory';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }
}
